import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Modal, Notice } from '@/components/Modal';
import { QrCodeModal } from '@/components/QrCodeModal';
import { ZOOM_INITIAL_SIZE, ZoomablePhoto } from '@/components/ZoomablePhoto';
import { apiFetch } from '@/lib/api/client';
import { sessionQueryKey, useLogout, useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';
import { USER_TYPE_LABEL } from '@/lib/types';
import { useMyProfile, type ProfileOrder, type ProfileUnit } from '@/pages/home/useMyProfile';
import { useMyDocumentFiles } from '@/pages/docs/useDocuments';
import { CareerTab } from '@/pages/mypage/CareerTab';
import { LeaveTab } from '@/pages/mypage/LeaveTab';
import { MyDutiesRow } from '@/pages/mypage/MyDutiesRow';
import { MyEducationRow } from '@/pages/mypage/MyEducationRow';
import { MyAssignedDutiesRow } from '@/pages/mypage/MyAssignedDutiesRow';
import { MyPhoneModal } from '@/pages/mypage/MyPhoneModal';
import { TotpRow } from '@/pages/mypage/TotpRow';
import { EducationTab } from '@/pages/mypage/EducationTab';
import { WelfareTab } from '@/pages/mypage/WelfareTab';
import { changeMyPhoto, uploadMyPhoto } from '@/pages/mypage/myPhoto';
import { safeHref } from '@/lib/utils/url';

/**
 * 마이페이지. `/mypage` 화면이다.
 *
 * <h2>탭은 다섯이다 (사용자 지시 2026-09-22 · 두 번째 정리)</h2>
 * 「개인정보 설정 / 교육 / 연차관리 / 복지 / 인사」.
 *
 * <pre>
 *   교육    복지 안에 있던 것을 <b>탭으로 꺼냈다</b> — 받는 것과 해야 하는 것은 여는 이유가 다르다
 *   연차관리 현황 + <b>대체휴무</b>(공휴일 근무) + <b>출퇴근</b>(앱이 찍고 여기서 본다)
 *   복지    경조·장학 / 수당 / 숙소 / 귀향여비 / 건강검진 / 개인자산 / 생일·복지몰 (+ 성과급 · 콘도)
 *   인사    경력 / 근무처이력 / 법정선임 / 자격증 / 근속기간 / 인사고과 / 명함신청
 * </pre>
 *
 * <p><b>자산 탭이 없어졌다</b> — 복지 안의 「개인자산」 으로 돌아갔다(사용자가 부른 목록 그대로).
 * 급여명세서는 2026-09-22 첫 정리에서 내렸고, 그 주석은 이 파일 아래에 그대로 있다.
 *
 * <h2>민감한 것은 여기서만 연다</h2>
 * 홈 대시보드는 어깨너머로 보이기 쉬운 화면이라, 인사고과는 본인이 의도적으로 들어오는 이 화면에서
 * 비밀번호를 한 번 더 받고 연다(`CareerTab`).
 *
 * 개인 인사정보(소속·직책·입사일)는 `useMyProfile` 이 읽는다 — 홈의 인사 위젯과 같은 훅이라
 * 요청은 한 번만 나간다.
 */

type TabKey = 'account' | 'education' | 'leave' | 'welfare' | 'career';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'account', label: '개인정보 설정' },
  // 교육은 <b>해야 하는 일</b>이라 복지(받는 것)에서 꺼냈다(2026-09-22). 법정의무교육과 기술인
  // 교육이 한 화면에 선다 — 근거가 되는 법은 달라도 사람이 하는 일은 하나다.
  { key: 'education', label: '교육' },
  { key: 'leave', label: '연차관리' },
  { key: 'welfare', label: '복지' },
  // 「인사」는 경력·법정선임·자격증·근속·고과·명함을 하위 탭으로 나눠 담는다. 서로가 서로의
  // 근거라 한 탭 안이되(자격증→선임→직책수당, 자격점수·고과→승진), 여는 이유는 하나씩이다.
  { key: 'career', label: '인사' },
];

export function MyPage() {
  /**
   * 첫 탭은 <b>주소가 정할 수 있다</b>(`/mypage?tab=leave`).
   *
   * <p>대화(AI)와 알림이 「연차관리로 가세요」 대신 <b>그 탭을 곧바로 열어</b> 준다 — 안내만 하면
   * 사용자가 마이페이지에서 탭을 다시 찾아야 하고, 그 한 걸음에서 절반이 떨어져 나간다.
   * 모르는 값이면 조용히 기본 탭이다 — 오래된 링크로 빈 화면을 띄우지 않는다.
   */
  const [params] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(
    () => TABS.find((item) => item.key === params.get('tab'))?.key ?? 'account',
  );

  /*
    첫 렌더에만 읽으면 <b>이미 마이페이지에 있는 사람</b>에게는 링크가 아무 일도 하지 않는다 —
    주소는 바뀌는데 이 컴포넌트가 그대로 붙어 있어 초기값이 다시 계산되지 않기 때문이다.
    화면 안에서 탭을 손으로 누른 경우는 주소에 그 탭이 없으므로 여기서 되돌리지 않는다.
  */
  useEffect(() => {
    const next = TABS.find((item) => item.key === params.get('tab'))?.key;
    if (next) setTab(next);
  }, [params]);

  /*
    잠금 상태(재인증 결과)는 이제 <b>인사 탭이</b> 쥔다(`CareerTab`). 여기 있던 이유는 급여·고과
    두 탭이 한 번의 확인으로 함께 열려야 했기 때문인데, 급여명세서를 내리면서 그 자료를 보는 탭이
    하나만 남았다. 쥐는 자리가 그 탭이면 탭을 떠나는 순간 다시 잠긴다.
  */

  return (
    <div className="space-y-3">
      {/*
        제목과 탭을 한 카드에 넣는다. 예전에는 카드 셋(제목 · 탭 · 본문)이 세로로 서서, 실제 내용이
        시작하기 전에 화면의 3분의 1이 사라졌다. 탭은 제목의 일부이지 별도의 블록이 아니다.
      */}
      <div className="card px-4 pt-4 pb-0 md:px-5">
        <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>마이페이지</h1>

        {/*
          탭은 다섯이다(2026-09-22 전에는 아홉). 데스크톱에서는 한 줄에 서지만 <b>모바일에서는
          두 줄로 접혀</b>, 화면을 열자마자 절반이 탭이고 정작 내용은 스크롤해야 보였다(사용자 지적
          2026-08-31).

          <p>그래서 <b>좁은 화면에서는 고르는 칸 하나</b>로 바꾼다. 줄바꿈을 줄이는 대신 늘 한 줄이고,
          지금 어느 탭인지가 칸 안에 그대로 적힌다 — 접힌 탭 줄에서는 선택된 탭이 둘째 줄에 숨어
          있어 «지금 어디» 를 알려면 눈으로 찾아야 했다.

          <p>브라우저의 기본 고르개(`select`)를 쓴다. 직접 만든 드롭다운은 모바일에서 화면 밖으로
          잘리거나 스크롤과 싸우는데, 기본 고르개는 <b>OS 가 자기 방식으로</b> 띄워 준다(안드로이드는
          바텀시트, iOS 는 휠). 손에 익은 조작이라 설명이 필요 없다.

          <p>안내 투어가 이 자리를 짚으므로(`page-tabs`) 둘을 <b>한 상자에 담고</b> 이름표는 그
          상자에 붙인다 — 두 벌에 같은 이름을 달면 화면에 없는 쪽이 먼저 잡혀 투어가 빈 곳을 가리킨다.
        */}
        <div data-tour="page-tabs">
          <div className="md:hidden pt-3 pb-4">
            <select
              className="input-field"
              aria-label="마이페이지 화면 고르기"
              value={tab}
              onChange={(event) => setTab(event.target.value as TabKey)}
            >
              {TABS.map((item) => (
                <option key={item.key} value={item.key}>{item.label}</option>
              ))}
            </select>
          </div>

          <div className="hidden md:flex gap-1 flex-wrap mt-3" role="tablist">
            {TABS.map((item) => (
              <button
                key={item.key}
                role="tab"
                aria-selected={tab === item.key}
                onClick={() => setTab(item.key)}
                style={{
                  fontSize: '13px',
                  padding: '8px 12px',
                  fontWeight: tab === item.key ? 700 : 500,
                  color: tab === item.key ? 'var(--accent)' : 'var(--text-secondary)',
                  // 알약 대신 밑줄 탭. 테두리를 두르면 탭마다 상자가 생겨 카드 안에 카드가 겹쳐 보인다.
                  borderBottom: `2px solid ${tab === item.key ? 'var(--accent)' : 'transparent'}`,
                  marginBottom: '-1px',
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 모바일은 안쪽 여백을 한 단 줄인다 — 5(20px)를 좌우로 물면 좁은 화면에서 본문 폭이 그만큼 준다. */}
      <div className="card p-4 md:p-5 space-y-4">
        {tab === 'account' && <AccountTab />}
        {tab === 'education' && <EducationTab />}
        {tab === 'leave' && <LeaveTab />}
        {tab === 'welfare' && <WelfareTab />}
        {tab === 'career' && <CareerTab />}
      </div>
    </div>
  );
}

// ── 개인정보 설정: 계정 + 개인 인사정보 + 비밀번호 ──────────────────────────────

function AccountTab() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session, isPending } = useSession();
  const user = session?.user ?? null;
  const { person, units, scopes, locationName, orders, allowance } = useMyProfile();
  const [editingPhone, setEditingPhone] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  /** 열려 있는 모달 — 지금은 개인 자료 하나다. 닫으면 안쪽 화면이 통째로 사라진다. */
  const [openPanel, setOpenPanel] = useState<'documents' | null>(null);

  // 초기 비밀번호를 쓰는 계정은 펼친 채로 연다 — 그 사람이 이 화면에 온 이유가 그것이다.
  // 첫 렌더에는 세션이 아직 없을 수 있어 초기값이 아니라 효과로 켠다.
  const mustChangePassword = Boolean(session?.user?.mustChangePassword);
  useEffect(() => {
    if (mustChangePassword) setChangingPassword(true);
  }, [mustChangePassword]);

  const changePassword = useMutation({
    // 새 비밀번호를 두 번 받는 것은 오타를 잡기 위한 화면의 일이라, 서버에는 한 번만 보낸다.
    mutationFn: (input: { currentPassword: string; nextPassword: string }) =>
      apiFetch<void>('/v1/auth/password', { method: 'PUT', body: input }),
    onSuccess: () => {
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirm('');
      setChangingPassword(false);
      void queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      // 홈의 비밀번호 알림(초기 비밀번호·3개월 주기)은 서버가 계정 표를 보고 만든다. 다시 읽어야
      // 방금 바꾼 사실이 반영된다 — 홈에 돌아왔을 때 이미 한 일이 할일로 남아 있으면 안 된다.
      void queryClient.invalidateQueries({ queryKey: queryKeys.todoItems });
    },
  });

  const canChangePassword =
    Boolean(currentPassword.trim() && password.trim() && passwordConfirm.trim()) && !changePassword.isPending;

  const handleChangePassword = () => {
    if (!canChangePassword) return;
    if (password !== passwordConfirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    changePassword.mutate({ currentPassword, nextPassword: password });
  };

  const resetMessages = () => {
    setMismatch(false);
    changePassword.reset();
  };

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  /*
    소속은 **인사 배치표**(`units`)를 정본으로 삼는다. 계정 소속(`scopes`)은 사용자 관리 화면에서
    따로 넣어야 하는 값이라 비어 있는 계정이 많고, 그래서 이 자리가 늘 "소속 미등록" 이었다.
    직원찾기 조직도가 세는 근거도 배치표이므로, 남이 보는 내 소속과 내가 보는 내 소속이 같아진다.
    배치표도 계정 소속도 없으면 대표 소속 이름(locationName)으로 한 칸이라도 채운다.
  */
  const belongings = units.length > 0
    ? units.map((unit) => ({
        name: unit.name,
        // 겸직·파견은 <b>성격이 먼저</b>다. 「팀장」보다 이것이 앞이어야 소속 셋이 늘어선 줄을
        // 본직 하나 + 겸직 둘로 읽는다 — 그러지 않으면 소속이 셋인 사람으로 보인다.
        note: ASSIGNMENT_ROLE_NOTE[unit.assignmentRole] ?? (unit.teamLead ? '팀장' : unit.roleTitle),
      }))
    : scopes.length > 0
      ? scopes.map((scope) => ({ name: scope.scopeName, note: null }))
      : locationName
        ? [{ name: locationName, note: null }]
        : [];

  return (
    <>
      {/*
        명함 한 줄. 예전에는 "계정"(이름·아이디·직급·권한 4칸) · "프로필 사진" · "개인 인사정보"(소속)가
        각각 제목을 달고 세로로 서서 같은 사람을 세 번 소개했다. 사진 옆은 어차피 비는 자리이므로
        거기에 이름·직급·소속을 넣으면 세 덩어리가 한 줄이 된다.
      */}
      <div className="flex items-start gap-4">
        <ProfilePhoto name={user?.name ?? ''} photoUrl={person?.photoUrl ?? null} linked={Boolean(person)} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)' }}>{user?.name ?? '-'}</span>
            {user?.rank && <span className="badge badge-gray">{user.rank}</span>}
            {user?.userType && (
              <span className="badge badge-gray">{USER_TYPE_LABEL[user.userType] ?? user.userType}</span>
            )}
          </div>
          <p className="truncate" style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {user?.email ?? '-'}
          </p>
          <div className="flex flex-wrap gap-1" style={{ marginTop: '8px' }}>
            {belongings.length > 0 ? (
              belongings.map((item) => (
                <span key={item.name} className="badge badge-blue">
                  {item.name}
                  {item.note ? ` · ${item.note}` : ''}
                </span>
              ))
            ) : (
              <span className="badge badge-gray">소속 미등록</span>
            )}
          </div>
        </div>
      </div>

      {!person && (
        <Notice tone="warning">인사 정보가 연결되지 않았습니다. 인사관리에 등록을 요청해 주세요.</Notice>
      )}

      {/*
        수당 대상 여부. 인사발령이 확정되는 순간 여기 뜬다 — 예전에는 파견을 나가고도 자기가 수당
        대상인지 아무도 몰라 인사팀에 전화로 물었다. <b>금액은 적지 않는다</b>: 귀향비 산정은
        아직 코드가 없고(DOC-10 §A), 반쯤 계산한 숫자는 급여로 읽힌다.
      */}
      {/*
        카드를 <b>누르면 그 화면으로 간다</b>(사용자 지시 2026-08-27). 「수당 대상」 이라고 알려 놓고
        어디서 자세히 보는지는 말하지 않으면, 사람은 탭을 하나씩 열어 보다 만다.
      */}
      {allowance?.eligible && (
        <button
          type="button"
          className="w-full"
          style={{ textAlign: 'left' }}
          onClick={() => navigate('/mypage?tab=welfare&sub=allowance')}
        >
          <Notice tone="success">
            <span className="flex items-baseline gap-2 flex-wrap">
              <span>
                <b>수당 대상</b> — {allowance.unitName}
                {allowance.since ? ` (${allowance.since}~)` : ''}
                {allowance.note ? ` · ${allowance.note}` : ''}
              </span>
              <span className="ml-auto" style={{ whiteSpace: 'nowrap' }}>자세히 보기 →</span>
            </span>
            <span style={{ display: 'block', fontSize: '12px', marginTop: '2px' }}>
              지급 금액과 반영 시점은 인사·급여 담당이 확정합니다.
            </span>
          </Notice>
        </button>
      )}

      <OrderHistory orders={orders} />

      {/*
        인사 정보 — 테두리 친 표 한 장(사용자 지시 2026-11-02: 「담당업무도 별도 라인으로 넣지 말고 이 칸을 개선」).
        라벨과 값은 <b>같은 줄</b>이다. 담당업무는 맨 아래 한 줄을 통째로 쓰고, 그 자리에서 고친다({@link MyDutiesRow}).
      */}
      <dl className="my-info">
        {/*
          사번이 <b>첫 칸</b>이다(사용자 지시 2026-09-29). 증명서·경조금·복지몰처럼 남이 「사번이
          몇 번이세요」 를 묻는 자리가 있고, 그때 본인이 자기 화면에서 곧바로 읽어야 한다.

          값은 인력DB 엑셀로만 들어오므로(사번 대장) 아직 없는 사람은 「-」 다 — 「미등록」 같은
          딱지를 붙이지 않는다: 본인이 할 수 있는 일이 없는 칸이다.
        */}
        <InfoRow label="사번" value={person?.employeeNo} />
        <InfoRow label="직책" value={person?.position} />
        <InfoRow label="직무" value={person?.field} />
        <InfoRow label="입사일" value={person?.joinDate} />
        <InfoRow label="근속" value={tenure(person?.joinDate)} />
        <InfoRow label="등급" value={person?.grade?.split('|')[0]?.trim()} />
        {/* 비밀번호 초기화의 인증번호가 이 번호로 간다 — 본인이 인증번호로 확인해 넣는다(2026-10-07). */}
        <InfoRow
          label="연락처"
          value={person?.phone}
          action={
            <button onClick={() => setEditingPhone(true)} className="btn-secondary" style={cellButton}>
              변경
            </button>
          }
        />
        <InfoRow label="내선" value={person?.extension} />
        {/* 학력은 칸 하나 — 최종학력과 나누지 않는다(사용자 지시 2026-11-02). 고칠 때만 한 줄을 다 쓴다. */}
        <MyEducationRow />
        <MyDutiesRow />
      </dl>
      {editingPhone && <MyPhoneModal current={person?.phone ?? ''} onClose={() => setEditingPhone(false)} />}

      {/*
        로그인 보안 — 비밀번호 · 구글 OTP 두 칸(사용자 지시 2026-11-03 「쓸데없이 width 만 차지」 ·
        「하나로 묶일 데이터가 아니다」). 식당 폰 · 개인 자료는 성격이 달라 아래 버튼 줄로 갈랐다.
        비밀번호 입력은 [변경] 의 모달이다. 초기 비밀번호를 쓰는 계정은 처음부터 열린 채로 시작한다.
      */}
      <p style={sectionTitle}>로그인 보안</p>
      <dl className="my-info my-info-2">
        <div>
          <dt>비밀번호</dt>
          <dd className="truncate">
            {user?.mustChangePassword ? (
              <span className="badge badge-yellow">초기 비밀번호</span>
            ) : changePassword.isSuccess ? (
              <span style={hintOk}>변경했습니다</span>
            ) : (
              '-'
            )}
          </dd>
          <span className="my-info-action">
            <button className="btn-secondary" style={cellButton} onClick={() => setChangingPassword(true)}>
              변경
            </button>
          </span>
        </div>
        {/* 구글 OTP(DOC-142) · 로그인 OTP(V618). */}
        <TotpRow />
      </dl>

      {/*
        그 밖 — 식당 폰 등록 · 개인 자료, 오른쪽 끝에 로그아웃.
        개인 자료는 모달이다(2026-09-22) — 따로 들어가 끝내고 나오는 일이다.
        증명서 발급은 인사 탭, 하이웍스 문은 헤더 [메일함](DOC-108). 누르기 전에는 요청이 나가지 않는다.
      */}
      <div className="flex items-center gap-2 flex-wrap">
        <MealDeviceRow />
        <button className="btn-secondary" style={rowButton} onClick={() => setOpenPanel('documents')}>
          개인 자료
        </button>
        <LogoutRow />
      </div>

      {changingPassword && (
        <Modal
          title="비밀번호 변경"
          maxWidth="380px"
          onClose={() => setChangingPassword(false)}
          actions={
            <button className="btn-primary" onClick={handleChangePassword} disabled={!canChangePassword}>
              {changePassword.isPending ? '변경 중...' : '변경'}
            </button>
          }
        >
          <div className="space-y-2">
            {/* 현재 비밀번호를 함께 받는다 — 자리를 비운 화면 앞에서 계정이 통째로 넘어가지 않게. */}
            <div className="space-y-2">
              <input
                className="input-field"
                type="password"
                autoComplete="current-password"
                aria-label="현재 비밀번호"
                value={currentPassword}
                onChange={(event) => {
                  setCurrentPassword(event.target.value);
                  resetMessages();
                }}
                placeholder="현재 비밀번호"
              />
              <input
                className="input-field"
                type="password"
                autoComplete="new-password"
                aria-label="새 비밀번호"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  resetMessages();
                }}
                placeholder="새 비밀번호"
              />
              <input
                className="input-field"
                type="password"
                autoComplete="new-password"
                aria-label="새 비밀번호 확인"
                value={passwordConfirm}
                onChange={(event) => {
                  setPasswordConfirm(event.target.value);
                  resetMessages();
                }}
                placeholder="새 비밀번호 확인"
                onKeyDown={(event) => event.key === 'Enter' && !event.nativeEvent.isComposing && handleChangePassword()}
              />
            </div>

            {mismatch && <p style={hintError}>비밀번호가 다릅니다.</p>}
            {changePassword.isError && <p style={hintError}>{changePassword.error.message}</p>}
            {changePassword.isSuccess && <p style={hintOk}>비밀번호를 변경했습니다.</p>}
          </div>
        </Modal>
      )}

      {openPanel === 'documents' && (
        <Modal title="개인 자료" onClose={() => setOpenPanel(null)} maxWidth="720px">
          <DocumentsTab />
        </Modal>
      )}

      {/* 업무분장은 맨 아래(사용자 지시 2026-11-03). 처음엔 접혀 건수만 보인다. */}
      <MyAssignedDutiesRow />
    </>
  );
}

/**
 * 식당 폰 등록 (사용자 지시 2026-09-21 · V311).
 *
 * <h2>왜 여기인가</h2>
 * 식당 QR 을 처음 찍는 폰에는 아무 자격이 없어서 그 자리에서 메일 주소와 비밀번호를 쳐야 했다 —
 * 배식 줄에서 폰 자판으로 할 일이 아니다. 그 한 번을 <b>미리</b> 끝내는 자리이고, 식대 관리
 * 화면은 총무의 메뉴라 정작 밥을 먹는 사람이 못 연다. 계정을 다루는 것들 맨 아래에 둔다.
 *
 * <p>이름은 <b>식당 폰 등록</b>이다(사용자 지시 2026-10-07). 2026-09-21 에는 「휴대폰 등록」 이었는데, 인증번호를 받을
 * 휴대폰 번호 등록(연락처 [변경])과 헷갈려 바꿨다.
 *
 * <p>화면에 뜬 QR 을 자기 폰으로 찍으면 그 폰이 기억된다(3분·1회용). 그 뒤로는 식당에서 찍기만
 * 하면 되고, <b>폰을 바꾸지 않는 한 다시 등록할 일이 없다</b>.
 */
function MealDeviceRow() {
  const [url, setUrl] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => apiFetch<{ token: string; expiresInSeconds: number }>('/v1/meals/pairing', { method: 'POST' }),
    // 주소는 화면이 조립한다 — 서버가 만들면 운영 주소를 설정값으로 또 들고 있어야 한다.
    onSuccess: (data) => setUrl(`${window.location.origin}/meals/pair?p=${encodeURIComponent(data.token)}`),
  });

  return (
    <>
      <button className="btn-secondary" style={rowButton} onClick={() => open.mutate()} disabled={open.isPending}>
        {open.isPending ? '띄우는 중...' : '식당 폰 등록'}
      </button>
      {open.isError && <span style={hintError}>{open.error.message}</span>}

      {url && (
        <QrCodeModal
          title="식당 폰 등록"
          url={url}
          hint="휴대폰 카메라로 찍으면 그 폰에서 점심 체크가 됩니다 · 3분간 유효"
          onClose={() => setUrl(null)}
        />
      )}
    </>
  );
}

/**
 * 로그아웃. 헤더에서 여기로 내려왔다(사용자 지시 2026-09-17) — 헤더는 이동하는 줄이라
 * 「직원찾기」·「?」 바로 옆에서 되돌릴 수 없는 동작이 눌렸다.
 *
 * <p>로그인 보안 표 아래 버튼 줄의 <b>오른쪽 끝</b>이다(2026-11-03). 식당 폰 · 개인 자료와 떨어져 있어
 * 손이 스쳐 눌리지 않는다.
 */
function LogoutRow() {
  const logout = useLogout();

  return (
    <div className="ml-auto">
      <button
        className="btn-secondary"
        style={rowButton}
        disabled={logout.isPending}
        onClick={() =>
          logout.mutate(undefined, {
            // 세션이 사라진 뒤 남아 있던 화면이 401 을 뿜지 않도록 곧바로 로그인 화면으로 보낸다.
            onSettled: () => window.location.assign('/login'),
          })
        }
      >
        {logout.isPending ? '로그아웃 중…' : '로그아웃'}
      </button>
    </div>
  );
}

// ── 연차관리는 별도 파일이다 ─────────────────────────────────────────────────
//
// 화면이 셋(현황 · 연 캘린더 · 촉진)으로 늘면서 이 파일 안에 두기에 커졌다. `LeaveTab.tsx` 로
// 옮기면서 **읽는 경로도 바꿨다** — 홈 위젯과 같은 `useHrProfile`(잔여 한 줄 요약)이 아니라
// `GET /v1/mypage/leave` 를 본다. 의무사용·수당 대상 일수·소멸은 위젯이 쓰지 않는 값이라
// 그쪽 응답에 실으면 홈이 무거워진다(DOC-47 §3.2).

// ── 급여명세서는 내렸다 (사용자 지시 2026-09-22) ──────────────────────────────
//
// 「급여명세서 탭은 제거하고」. 화면에서 <b>지우지 않고 주석으로 남긴다</b>(사용자 결정 2026-09-22
// — 「전부 주석처리」): 서버의 명세서 자료·업로드 경로는 그대로 있고, 다시 열자는 말이 나오면
// 이 스무 줄을 되살리는 일이 된다. 지워 버리면 같은 화면을 처음부터 다시 만든다.
//
// 인사고과는 <b>인사 탭 안으로</b> 옮겼다(`CareerTab`). 승진 요건이 「현 직급 고과 합계 N점」 을
// 말하는 자리 바로 옆이라, 등급을 보려고 탭을 옮겨 다닐 이유가 없다. 재인증(`PasswordGate`)과
// 「여기까지 봤다」 표시도 함께 갔다 — 그 자료를 여는 유일한 자리가 거기다.
//
// function PayslipTab({ sensitive, onUnlock }: SensitiveTabProps) {
//   const profile = useHrProfile();
//   useMarkReadOnUnlock('PAYSLIP', Boolean(sensitive));
//
//   if (!sensitive) {
//     return <PasswordGate label="급여명세서" onUnlock={onUnlock} available={profile.data?.hasPayslips ?? false} />;
//   }
//   if (sensitive.payslips.length === 0) {
//     return <p className="py-8 text-center" style={{ fontSize: '13px', color: 'var(--text-muted)' }}>등록된 급여명세서가 없습니다.</p>;
//   }
//
//   return (
//     <div className="space-y-1">
//       {sensitive.payslips.map((slip) => (
//         <div
//           key={slip.payMonth}
//           className="flex items-center gap-3 rounded-lg px-3 py-2.5"
//           style={{ border: '1px solid var(--border)' }}
//         >
//           <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', width: '72px' }}>
//             {slip.payMonth}
//           </span>
//           {/* 금액은 예전에 입력된 건에만 있다. 지금은 명세서 파일이 원본이라 따로 받지 않는다. */}
//           <span className="flex-1 text-right" style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
//             {slip.netAmount == null ? '' : `${slip.netAmount.toLocaleString('ko-KR')}원`}
//           </span>
//           {slip.documentUrl ? (
//             <a href={safeHref(slip.documentUrl)} target="_blank" rel="noreferrer" className="badge badge-blue" style={{ fontSize: '11px', flexShrink: 0 }}>
//               명세서 보기
//             </a>
//           ) : (
//             <span className="badge badge-gray" style={{ fontSize: '11px', flexShrink: 0 }}>
//               {slip.hasDocument ? '명세서' : '준비중'}
//             </span>
//           )}
//         </div>
//       ))}
//     </div>
//   );
// }

// ── 개인 자료: 자료실에서 나에게 귀속된 문서 ──────────────────────────────────

function DocumentsTab() {
  const { data: files, isPending, error } = useMyDocumentFiles();

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  if (error) {
    return <Notice tone="error">개인 자료를 불러오지 못했습니다.</Notice>;
  }
  if (!files || files.length === 0) {
    return <p className="py-8 text-center" style={{ fontSize: '13px', color: 'var(--text-muted)' }}>등록된 개인 자료가 없습니다.</p>;
  }
  return (
    <div className="space-y-1">
      {files.map((file) => (
        <div
          key={file.id}
          className="flex items-center gap-3 rounded-lg px-3 py-2.5"
          style={{ border: '1px solid var(--border)' }}
        >
          <span className="flex-1 min-w-0">
            <span className="block truncate" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {file.originalName}
            </span>
            <span className="block truncate" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {file.categoryName ?? file.categoryCode ?? ''} · {file.storedName}
            </span>
          </span>
          {file.downloadUrl ? (
            <a href={safeHref(file.downloadUrl)} target="_blank" rel="noreferrer" className="badge badge-blue" style={{ fontSize: '11px', flexShrink: 0 }}>
              열기
            </a>
          ) : (
            <span className="badge badge-gray" style={{ fontSize: '11px', flexShrink: 0 }}>준비중</span>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * 프로필 사진. 헤더의 <b>직원찾기</b> 에 보이는 그 사진이고, 본인이 여기서 바꾼다.
 *
 * 폼의 다른 칸과 달리 <b>고르는 즉시 저장된다</b>. 파일이 스토리지로 직행하고 서버에는 키만 남는
 * 구조라, 저장 버튼까지 들고 있으려면 취소했을 때 올라간 파일을 되돌리는 정리 작업이 따로 필요해진다.
 *
 * 인사 명부와 연결되지 않은 계정은 사진을 걸 자리가 없다 — 서버도 같은 이유로 거절한다.
 */
function ProfilePhoto({ name, photoUrl, linked }: { name: string; photoUrl: string | null; linked: boolean }) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<string | null>(photoUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 서버에서 다시 읽어 온 값이 로컬 미리보기보다 새로우면 그쪽을 따른다(다른 탭에서 바꾼 경우).
  const [seen, setSeen] = useState(photoUrl);
  if (photoUrl !== seen) {
    setSeen(photoUrl);
    setPreview(photoUrl);
  }

  const run = async (task: () => Promise<string | null>) => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await task());
      void queryClient.invalidateQueries({ queryKey: queryKeys.homeProfile });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '사진을 저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const box = { width: '64px', height: '80px', borderRadius: '8px' } as const;

  /** 사진이 없을 때의 이니셜 칸. 작게(64) 한 번, 확대 창에서 크게 한 번 — 같은 모양이어야 한다. */
  const initialBox = (width: number) => (
    <span
      aria-hidden
      className="flex items-center justify-center"
      style={{
        width: `${width}px`,
        height: `${Math.round(width * 1.25)}px`,
        borderRadius: '8px',
        background: 'var(--bg-hover)',
        color: 'var(--text-muted)',
        fontSize: `${Math.round(width * 0.375)}px`,
        fontWeight: 700,
      }}
    >
      {name.slice(0, 1) || '?'}
    </span>
  );

  return (
    <div className="flex-shrink-0" style={{ width: '64px' }}>
      {/* 누르면 크게 뜬다 — 64×80 으로는 내가 올린 사진이 제대로 나왔는지 알 수 없다(2026-09-28). */}
      <ZoomablePhoto
        src={preview}
        name={name || '내'}
        style={{ ...box, objectFit: 'cover', border: '1px solid var(--border)' }}
        initial={initialBox(64)}
        initialLarge={initialBox(ZOOM_INITIAL_SIZE)}
      />

      {/*
        설명 문구를 두지 않는다. "사진을 올린다" 는 것은 사진 자리와 버튼만 보면 알 수 있고,
        그 한 문장이 명함 오른쪽 자리를 통째로 차지하고 있었다. 안내는 막힐 때만 띄운다.
      */}
      <div className="flex items-center justify-center gap-1" style={{ marginTop: '5px' }}>
        <label
          style={{
            fontSize: '11px',
            color: linked ? 'var(--accent)' : 'var(--text-muted)',
            cursor: busy || !linked ? 'default' : 'pointer',
          }}
          title={linked ? undefined : '인사 정보가 연결된 뒤에 등록할 수 있습니다.'}
        >
          {busy ? '처리 중' : preview ? '변경' : '등록'}
          <input
            type="file"
            accept="image/*"
            hidden
            disabled={busy || !linked}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // 같은 파일을 다시 골랐을 때도 onChange 가 뜨도록 값을 비운다.
              event.target.value = '';
              if (file) void run(async () => changeMyPhoto(await uploadMyPhoto(file)));
            }}
          />
        </label>
        {preview && (
          <>
            <span style={{ fontSize: '11px', color: 'var(--border-strong)' }}>·</span>
            <button
              style={{ fontSize: '11px', color: 'var(--text-muted)' }}
              disabled={busy}
              onClick={() => void run(() => changeMyPhoto(null))}
            >
              삭제
            </button>
          </>
        )}
      </div>
      {error && <p style={{ ...hintError, textAlign: 'center' }}>{error}</p>}
    </div>
  );
}

/**
 * 소속 뱃지에 붙는 성격. 본직은 아무것도 붙이지 않는다 — 대부분의 사람에게 소속은 하나뿐이고,
 * 거기에 「본직」이 붙어 있으면 그 말이 무엇과 대비되는지 알 수 없다.
 */
const ASSIGNMENT_ROLE_NOTE: Record<ProfileUnit['assignmentRole'], string | null> = {
  PRIMARY: null,
  CONCURRENT: '겸직',
  DISPATCH: '파견',
};

const ORDER_TYPE_TEXT: Record<ProfileOrder['orderType'], string> = {
  TRANSFER: '전보',
  CONCURRENT: '겸직',
  DISPATCH: '파견',
  RELEASE: '해제',
};

/**
 * 내 인사발령 — <b>내가 왜 지금 여기 있는가</b>.
 *
 * 인력배치표의 「변경 이력」과 같은 사건을 본인 눈으로 본 것이다. 남의 발령은 오지 않는다
 * (경로에 사용자 id 가 없다 — `HomeProfileController` 주석). 발령이 없으면 이 칸 자체가 없다.
 */
function OrderHistory({ orders }: { orders: ProfileOrder[] }) {
  if (orders.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
      <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '6px' }}>
        최근 인사발령
      </h3>
      <div className="flex flex-col" style={{ gap: '4px' }}>
        {orders.map((order) => (
          <div
            key={`${order.effectiveDate}-${order.toName ?? 'release'}-${order.orderType}`}
            className="flex items-center gap-2 flex-wrap"
            style={{ fontSize: '12px', color: 'var(--text-secondary)' }}
          >
            <span style={{ color: 'var(--text-muted)', minWidth: '80px' }}>{order.effectiveDate}</span>
            <span className="badge badge-gray" style={{ fontSize: '10px' }}>{ORDER_TYPE_TEXT[order.orderType]}</span>
            <span>
              {order.fromName ?? '미배치'} → {order.toName ?? '해제'}
              {order.endDate ? ` (~${order.endDate})` : ''}
            </span>
            {/*
              아직 오지 않은 발령이다(V247). 채운 뱃지가 아니라 <b>밑줄 테두리</b>로 그린다 —
              지나간 발령과 같은 모양이면 본인이 이미 옮겨 간 줄로 읽는다. 그날 00시에 소속이
              바뀌므로, 미리 보이지 않으면 아침에 자기 소속이 달라져 있어 당황한다.
            */}
            {order.status === 'SCHEDULED' && (
              <span
                style={{
                  fontSize: '10px',
                  color: 'var(--text-muted)',
                  borderBottom: '2px dashed var(--border-strong)',
                  padding: '0 1px',
                }}
              >
                예정
              </span>
            )}
            {order.allowanceEligible && <span className="badge badge-blue" style={{ fontSize: '10px' }}>수당 대상</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** 라벨과 값이 한 줄. 값이 길면(이메일·소속) 줄바꿈 대신 잘라 낸다 — 표의 줄 높이가 들쭉날쭉해진다. */
function InfoRow({ label, value, action }: { label: string; value?: string | null; action?: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className="truncate">{value || '-'}</dd>
      {action && <span className="my-info-action">{action}</span>}
    </div>
  );
}

const rowButton = { fontSize: '12px', padding: '5px 12px' } as const;
/** 정보 표 칸 안의 작은 버튼 — 칸 높이를 늘리지 않는다. */
const cellButton = { fontSize: '12px', padding: '3px 10px' } as const;
const hintError = { fontSize: '12px', color: 'var(--danger)' } as const;
/** 묶음 제목(로그인 보안). 표 바로 위에 붙는다 — 부모의 space-y 간격을 반쯤 되돌린다. */
const sectionTitle = { fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '-8px' } as const;
/** 성공 색은 CSS 변수에 없다(--danger 만 있다). 이 한 줄을 위해 변수를 늘리지 않는다. */
const hintOk = { fontSize: '12px', color: '#15803d' } as const;

/** `7년 4개월`. 입사일이 없으면 계산하지 않는다. */
function tenure(joinDate: string | null | undefined): string {
  if (!joinDate) return '-';

  const start = new Date(`${joinDate}T00:00:00`);
  if (Number.isNaN(start.getTime())) return '-';

  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '-';

  const years = Math.floor(months / 12);
  const rest = months % 12;
  return years > 0 ? `${years}년 ${rest}개월` : `${rest}개월`;
}
